import React, { useEffect, useState } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import { Card, CardBody, Row, Col, Input, Button } from "reactstrap"
import { Map, TileLayer, Polyline, Marker, Tooltip } from "react-leaflet"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import { fetchData, FetchPostData } from "utils/Common_api_functions"
import Swal from "sweetalert2"
import Loader from "components/Loader/Loader"
import {
  FaRoute,
  FaMapMarkerAlt,
  FaRoad,
  FaMoneyBillWave,
  FaUserAlt,
  FaCalendarAlt,
  FaSearch,
} from "react-icons/fa"
import { MdLocationOn, MdTimer } from "react-icons/md"
import PermittedComponent from "PermittedComponent"
import { usePermissionData } from "AppContext"

/* ================= ICONS ================= */

const createColoredIcon = color =>
  new L.DivIcon({
    className: "",
    html: `<div style="
      width:28px;height:28px;
      background:${color};
      border:3px solid #fff;
      border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);
      box-shadow:0 2px 8px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  })

const startIcon = createColoredIcon("#22c55e")
const endIcon = createColoredIcon("#EF5350")

const createNumberIcon = (color, text) =>
  new L.DivIcon({
    className: "",
    html: `<div style="
      background:${color};color:#fff;
      width:34px;height:34px;
      display:flex;align-items:center;justify-content:center;
      border-radius:50%;font-size:10px;font-weight:700;
      border:2px solid #fff;
      box-shadow:0 2px 6px rgba(0,0,0,0.25);
    ">${text}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 34],
  })

/* ================= STAT CARD ================= */

const StatCard = ({ icon, label, value, color, bg }) => (
  <div
    style={{
      background: bg || "#f8fafc",
      borderRadius: 14,
      padding: "14px 18px",
      display: "flex",
      alignItems: "center",
      gap: 14,
      border: `1px solid ${color}22`,
      boxShadow: `0 2px 8px ${color}18`,
      minWidth: 150,
      flex: 1,
    }}
  >
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: 12,
        background: `${color}18`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 20,
        color,
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div>
      <div
        style={{
          fontSize: 11,
          color: "#94a3b8",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          color: "#1e293b",
          lineHeight: 1.2,
        }}
      >
        {value}
      </div>
    </div>
  </div>
)

/* ================= PUNCH TIMELINE ================= */

const PunchTimeline = ({ attendance, onSelect, selectedIndex }) => {
  if (!attendance?.length) return null

  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: "#475569",
          marginBottom: 10,
          textTransform: "uppercase",
        }}
      >
        Punch Timeline
      </div>

      <div
        style={{ display: "flex", gap: 8, width: "100%", overflowX: "auto" }}
      >
        {attendance.map((item, i) => {
          const isActive = selectedIndex === i

          return (
            <div
              key={i}
              onClick={() => onSelect(i)}
              style={{
                background: isActive ? "#e0e7ff" : "#f8fafc",
                borderRadius: 12,
                padding: "10px 14px",
                borderLeft: `4px solid ${isActive ? "#4f46e5" : "#6366f1"}`,
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#6366f1",
                  minWidth: 40,
                }}
              >
                #{i + 1}
              </div>
              {item.in_latitude && (
                <div style={{ flex: 1, minWidth: 150 }}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "#22c55e",
                        display: "inline-block",
                      }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#22c55e",
                      }}
                    >
                      PUNCH IN
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    {item.punchInAddress || "—"}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    {item.inTime || "—"}
                  </div>
                </div>
              )}
              {item.out_latitude && (
                <div style={{ flex: 1, minWidth: 150 }}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "#EF5350",
                        display: "inline-block",
                      }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#EF5350",
                      }}
                    >
                      PUNCH OUT
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    {item.punchOutAddress || "—"}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    {item.outTime || "—"}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ================= MAIN COMPONENT ================= */

const LocationTracking = () => {
  const [employee, setEmployee] = useState("")
  const [date, setDate] = useState("")
  const [route, setRoute] = useState([])
  const [data, setData] = useState({})
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(false)

  const [selectedPunchIndex, setSelectedPunchIndex] = useState(null)

  const { checkAccess } = usePermissionData()

  useEffect(() => {
    getEmployees()
  }, [])

  const getEmployees = async () => {
    const res = await fetchData(setLoading, "users")
    if (res?.success) setEmployees(res.data)
  }

  const fetchLocationData = async () => {
    const res = await FetchPostData(
      setLoading,
      "attendance/user/route/by/date",
      { userId: employee, date }
    )
    if (res?.data?.success) {
      setData(res.data)
      setRoute(res.data.data.polyline || [])
    } else {
      Swal.fire("Error", res?.response?.data?.message, "error")
    }
  }

  const punchMarkers =
    data?.attendance?.flatMap((item, index) => {
      const arr = []

      if (item.in_latitude && item.in_longitude) {
        arr.push({
          position: [
            parseFloat(item.in_latitude),
            parseFloat(item.in_longitude),
          ],
          label: `IN ${index + 1}`,
          address: item.punchInAddress,
          color: "#22c55e",
          index,
        })
      }

      if (item.out_latitude && item.out_longitude) {
        arr.push({
          position: [
            parseFloat(item.out_latitude),
            parseFloat(item.out_longitude),
          ],
          label: `OUT ${index + 1}`,
          address: item.punchOutAddress,
          color: "#EF5350",
          index,
        })
      }

      return arr
    }) || []

  const totalTA =
    data?.expenses?.find(item => item?.type === "travel")?.value || 0
  const totalDA =
    data?.expenses?.find(item => item?.type === "dearness")?.value || 0
  const hasRoute = route.length > 0

  const selectedEmpName =
    employees.find(e => String(e.id) === String(employee))?.name || ""

  return (
    <PermittedComponent isAccess={checkAccess(7, "view")}>
      <div className="page-content">
        {loading && <Loader />}

        <Breadcrumb
          title="Employee Tracking"
          breadcrumbItem="Location Tracking"
        />

        {/* ── FILTER CARD ─────────────────────────────────────────────────── */}
        <Card
          style={{
            borderRadius: 16,
            border: "none",
            boxShadow: "0 2px 16px rgba(99,102,241,0.08)",
            marginBottom: 20,
          }}
        >
          <CardBody style={{ padding: "20px 24px" }}>
            <Row className="g-3 align-items-end">
              <Col md={2}>
                <Input
                  type="select"
                  className="form-select rounded-4"
                  onChange={e => setEmployee(e.target.value)}
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md={2}>
                <Input
                  type="date"
                  className="rounded-4"
                  onChange={e => setDate(e.target.value)}
                />
              </Col>

              <Col md={2}>
                <Button
                  disabled={!employee || !date}
                  onClick={fetchLocationData}
                  className="btn-blue"
                >
                  <FaRoute size={14} /> Track Route
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* ── RESULTS ─────────────────────────────────────────────────────── */}
        {hasRoute ? (
          <Row className="g-3">
            {/* ── LEFT: MAP ── */}
            <Col md={12}>
              <Card
                style={{
                  borderRadius: 16,
                  border: "none",
                  boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 60,
                    right: 20,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    height: "100%",
                    zIndex: 999,
                  }}
                >
                  {/* Stats */}
                  <Card
                    style={{
                      borderRadius: 16,
                      border: "1px solid black",
                    }}
                    className="shadow-lg"
                  >
                    <CardBody style={{ padding: 20 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          letterSpacing: 0.6,
                          marginBottom: 14,
                        }}
                      >
                        Trip Summary
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 10,
                        }}
                      >
                        <StatCard
                          icon={<FaRoad />}
                          label="Distance"
                          value={`${data?.data?.distance?.km ?? "—"} km`}
                          color="#6366f1"
                        />
                        <StatCard
                          icon={<FaMoneyBillWave />}
                          label="TA Allowance"
                          value={`₹${totalTA}`}
                          color="#22c55e"
                        />
                        <StatCard
                          icon={<FaMoneyBillWave />}
                          label="DA Allowance"
                          value={`₹${totalDA}`}
                          color="#f59e0b"
                        />
                      </div>
                    </CardBody>
                  </Card>
                </div>
                <div
                  style={{
                    position: "absolute",
                    bottom: -20,
                    width: "95%",
                    left: 20,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    zIndex: 999,
                  }}
                >
                  <Card
                    style={{
                      borderRadius: 16,
                      border: "none",
                      boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
                      flex: 1,
                    }}
                  >
                    <CardBody className="h-auto">
                      <PunchTimeline
                        attendance={data?.attendance}
                        selectedIndex={selectedPunchIndex}
                        onSelect={setSelectedPunchIndex}
                      />
                    </CardBody>
                  </Card>
                </div>
                {/* Map header */}
                <div
                  style={{
                    padding: "14px 20px",
                    background: "linear-gradient(135deg, #1e293b, #334155)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <MdLocationOn size={18} color="#6366f1" />
                    <span
                      style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}
                    >
                      {selectedEmpName || "Employee"} — Route Map
                    </span>
                  </div>
                  <div
                    style={{ display: "flex", gap: 12, alignItems: "center" }}
                  >
                    <LegendDot color="#22c55e" label="Start" />
                    <LegendDot color="#EF5350" label="End" />
                    <LegendDot color="#3b82f6" label="Route" isLine />
                  </div>
                </div>

                <CardBody style={{ padding: 0, height: 600 }}>
                  <Map
                    center={route[0]}
                    zoom={15}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <Polyline
                      positions={route}
                      color="#6366f1"
                      weight={4}
                      opacity={0.85}
                    />

                    <Marker position={route[0]} icon={startIcon}>
                      <Tooltip permanent direction="top">
                        Punch In
                      </Tooltip>
                    </Marker>
                    <Marker position={route[route.length - 1]} icon={endIcon}>
                      <Tooltip permanent direction="top">
                        Punch Out
                      </Tooltip>
                    </Marker>

                    {punchMarkers.map((m, i) => {
                      const isActive = selectedPunchIndex === m.index

                      return (
                        <Marker
                          key={i}
                          position={m.position}
                          icon={createNumberIcon(
                            isActive ? "#4f46e5" : m.color,
                            m.label
                          )}
                          eventHandlers={{
                            click: () => setSelectedPunchIndex(m.index),
                          }}
                        >
                          <Tooltip direction="top" offset={[0, -20]}>
                            <div style={{ fontSize: 11, maxWidth: "100%" }}>
                              <b>{m.label}</b>
                              <br />
                              {m.address || "No Address"}
                            </div>
                          </Tooltip>
                        </Marker>
                      )
                    })}
                  </Map>
                </CardBody>
              </Card>
            </Col>

            {/* ── RIGHT: STATS + TIMELINE ── */}
            <Col md={4}></Col>
          </Row>
        ) : (
          /* ── EMPTY STATE ── */
          <Card
            style={{
              borderRadius: 16,
              border: "none",
              boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
            }}
          >
            <CardBody>
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  color: "#94a3b8",
                }}
              >
                <FaRoute size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#64748b",
                    marginBottom: 6,
                  }}
                >
                  No Route Found
                </div>
                <div style={{ fontSize: 13 }}>
                  Select an employee and date, then click Track Route.
                </div>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    </PermittedComponent>
  )
}

/* ── tiny legend helper ── */
const LegendDot = ({ color, label, isLine }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
    {isLine ? (
      <div
        style={{ width: 18, height: 3, background: color, borderRadius: 2 }}
      />
    ) : (
      <div
        style={{ width: 9, height: 9, borderRadius: "50%", background: color }}
      />
    )}
    <span style={{ fontSize: 11, color: "#cbd5e1", fontWeight: 500 }}>
      {label}
    </span>
  </div>
)

export default LocationTracking
